import { DomainError, RuleViolationError } from '../errors.js';
import type { MessageKind } from './attachments.js';
import type { CallTransport } from './sfu.js';
import type { ConversationStatus } from './stateMachine.js';

export const MESSAGE_BODY_MAX_CHARS = 4000;
export const GROUP_TITLE_MAX_CHARS = 120;
export const CALL_PARTICIPANT_CAP = 25;
export const CALL_CAMERA_OFF_ABOVE = 5;
export const CALL_RING_TIMEOUT_MS = 45_000;
export const CALL_STALE_RING_MS = 90_000;
export const SIGNAL_PAYLOAD_MAX_BYTES = 64 * 1024;

/**
 * Two people share exactly one direct conversation, whichever of them opened it.
 * The key is order-independent so a race between both sides lands on the same
 * unique value and the second insert collides instead of creating a twin.
 */
export function directKey(personA: string, personB: string): string {
  if (personA === personB) {
    throw new RuleViolationError(
      'chat.self_conversation',
      'A direct conversation needs two people',
    );
  }
  return [personA, personB].sort().join(':');
}

export function normaliseMessageBody(raw: string): string {
  const body = raw.replace(/\r\n/g, '\n').trim();
  if (body.length === 0) {
    throw new RuleViolationError('chat.empty_message', 'A message needs some text');
  }
  if (body.length > MESSAGE_BODY_MAX_CHARS) {
    throw new RuleViolationError('chat.message_too_long', 'Message is too long', {
      max: MESSAGE_BODY_MAX_CHARS,
      length: body.length,
    });
  }
  return body;
}

export function normaliseGroupTitle(raw: string): string {
  const title = raw.replace(/\s+/g, ' ').trim();
  if (title.length === 0) {
    throw new RuleViolationError('chat.empty_title', 'A group needs a title');
  }
  if (title.length > GROUP_TITLE_MAX_CHARS) {
    throw new RuleViolationError('chat.title_too_long', 'Group title is too long', {
      max: GROUP_TITLE_MAX_CHARS,
      length: title.length,
    });
  }
  return title;
}

/**
 * Mesh calls put one upstream per peer on every laptop. The cap is a safety
 * stop for the mesh (decided 2026-10-25: 25, up from 5); an SFU call has no
 * cap. Above a handful of people the camera starts off either way.
 */
export function assertCallHasRoom(joinedCount: number, transport: CallTransport): void {
  if (transport === 'mesh' && joinedCount >= CALL_PARTICIPANT_CAP) {
    throw new RuleViolationError('chat.call_full', 'This call is full', {
      cap: CALL_PARTICIPANT_CAP,
    });
  }
}

/** Whether someone joining a call with this many others already in it starts with the camera off. */
export function cameraOffByDefault(othersJoined: number): boolean {
  return othersJoined >= CALL_CAMERA_OFF_ABOVE;
}

/**
 * A mention only counts for someone who is actually in the conversation;
 * anything else is dropped silently rather than notifying an outsider.
 */
export function effectiveMentions(
  requested: readonly string[],
  memberIds: ReadonlySet<string>,
  authorId: string,
): string[] {
  const seen = new Set<string>();
  for (const id of requested) {
    if (id !== authorId && memberIds.has(id)) seen.add(id);
  }
  return [...seen];
}

export function isUnreadFor(
  message: { id: bigint; authorId: string | null },
  personId: string,
  lastReadMessageId: bigint | null,
): boolean {
  if (message.authorId === personId) return false;
  return lastReadMessageId === null || message.id > lastReadMessageId;
}

/** A caption may be empty; otherwise it is a message body. */
export function normaliseCaption(raw: string | undefined | null): string {
  const caption = (raw ?? '').replace(/\r\n/g, '\n').trim();
  return caption.length === 0 ? '' : normaliseMessageBody(caption);
}

/**
 * Editing is the author's alone and only where a body exists to change: text
 * and file captions. A redacted message has no body any more, and an archived
 * group is read-only.
 */
export function assertEditable(
  message: { kind: MessageKind; authorId: string | null; redactedAt: Date | null },
  actorId: string,
  conversationStatus: ConversationStatus,
): void {
  if (conversationStatus !== 'active') {
    throw new RuleViolationError('chat.conversation_archived', 'This group is archived');
  }
  if (message.kind !== 'text' && message.kind !== 'file') {
    throw new RuleViolationError('chat.edit_kind', `A ${message.kind} message cannot be edited`);
  }
  if (message.redactedAt !== null) {
    throw new RuleViolationError('chat.edit_redacted', 'This message was unsent');
  }
  if (message.authorId !== actorId) {
    throw new DomainError('forbidden', 'Only the author can edit a message', 403);
  }
}

/** People mentioned by the edit who were not mentioned before; only they are notified. */
export function newMentions(previous: readonly string[], next: readonly string[]): string[] {
  const before = new Set(previous);
  return [...new Set(next)].filter((id) => !before.has(id));
}
